import {
  GroupChatResponseType,
  P2PChatResponseType,
} from "@/server/types/chat";
import { Member } from "@/server/types/member";
import { EncryptedMessage } from "@/server/types/message";
import { EncryptedUserData } from "@/server/types/user";

// TO DO => MERGE TYPES FROM SERVER TYPES since they are the same
enum SocketEventTypes {
  HEARTBEAT = "heartbeat",
  USER = "user",
  CHAT = "chat",
  P2P_CHAT = "p2p-chat",
  CHAT_WITH_MEMBERS = "chat-with-members", // GROUP CHAT EVENTS
  MEMBER = "member",
  CHAT_MEMBER = "chat-member",
  GROUP_REQUEST = "group-request",
  MESSAGE = "message",
  PINNED_MESSAGE = "pinned-message",
  REACTION = "reaction",
  RESET_CHAT_COUNTER = "reset-chat-counter",
  TYPING = "typing",
  MEETING_STARTED = "meeting-started",
  MEETING_ENDED = "meeting-ended",
  PARTICIPANT_JOINED = "participant-joined",
  PARTICIPANT_LEFT = "participant-left",
  ROOM_FULL = "room-full",
  GET_ACTIVE_ROOMS = "get-active-rooms",
  GET_PUSH_PUBKEY = "get-push-pubkey",
  FETCH_EVENTS = "fetch-events",
  DELETE_SUBSCRIPTION = "delete-subscription",
  ADD_SUBSCRIPTION = "add-subscription",
  READ_SUBSCRIPTIONS = "read-subscriptions",
  SUBSCRIPTION_UPDATED = "subscription_updated",
  KEY_ROTATED = "key-rotated",
  ROTATION_REQUESTED = "rotation-requested",
}

enum SocketEventActionType {
  CREATE = "Create",
  UPDATE = "Update",
  DELETE = "Delete",
}

// USER EVENTS
type UserEventType = {
  action: SocketEventActionType;
  user: EncryptedUserData;
};

// CALL EVENTS
type MeetingEventType = {
  room: string;
  participant_count: number;
};

type ParticipantEventType = {
  room: string;
  participant_count: number;
  username: string;
};

// Emitted by the broker when a join is rejected because the room hit its
// subscription participant limit. Mirrors JitsiData on the backend
// (broker/src/pubsub.rs -> "room-full"): same room/username/participant_count
// as participant-joined, plus an occupant_jid and a human-readable `extra`.
type RoomFullEventType = {
  room: string;
  participant_count: number;
  username: string | null;
  occupant_jid: string | null;
  extra: string | null;
};

type RoomData = {
  chat_id: string;
  participants: string[];
  participant_coount: number;
};

// GROUP REQUEST EVENTS
type GroupRequestEventType = {
  action: SocketEventActionType;
  request: { chat_id: string; username: string; public_keys: string };
};

// MESSAGE EVENTS
type MessageEventType = {
  action: SocketEventActionType;
  message: EncryptedMessage;
};

// REACTION EVENTS
type ReactionEventType = {
  action: SocketEventActionType;
  reaction: {
    id: string;
    chat_id: string;
    message_id: string;
    content: string;
    sender: string;
  };
};

type Umbra_ReactionEventType = {
  action: SocketEventActionType;
  reaction: {
    chat_id: string;
    message_id: string;
    reaction: string;
    summary: Record<string, number>;
  };
};

// GROUP CHAT EVENTS
type ChatWithMembersEventType = {
  action: SocketEventActionType;
  chat_members: {
    chat: GroupChatResponseType;
    members: Member[];
  };
};

// P2P CHAT EVENTS
type P2PChatEventType = {
  action: SocketEventActionType;
  p2p_chat: P2PChatResponseType;
};

// CHAT EVENTS => this is actually for Group Chats - From BE
type ChatEventType = {
  action: SocketEventActionType;
  chat: GroupChatResponseType;
};

// CHAT MEMBERS EVENTS
type ChatMemberEventType = {
  action: SocketEventActionType;
  chat_member: {
    chat: GroupChatResponseType;
    member: Member;
    pinned_messages: {
      chat_id: string;
      message_id: string;
      message: EncryptedMessage;
    }[];
  };
};

// MEMBER EVENTS
type MemberEventType = {
  action: SocketEventActionType;
  member: Member;
};

// RESET COUNTER EVENT TYPE
type ResetCounterEventType = {
  reset_chat_counter: {
    username: string;
    chat_id: string;
  };
};

// TYPING EVENTS (broker -> client broadcast). Ephemeral, unencrypted, not stored.
// Mirrors the broker contract: { chat_id, username, is_typing }.
type TypingEventType = {
  chat_id: string;
  username: string;
  is_typing: boolean;
};

export { SocketEventActionType, SocketEventTypes };

export type {
  GroupRequestEventType,
  UserEventType,
  MeetingEventType,
  ParticipantEventType,
  RoomFullEventType,
  RoomData,
  MessageEventType,
  ReactionEventType,
  ResetCounterEventType,
  TypingEventType,
  ChatWithMembersEventType,
  P2PChatEventType,
  ChatEventType,
  ChatMemberEventType,
  MemberEventType,
  // umbra stuff
  Umbra_ReactionEventType,
};
